import React, { useState, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotificationCenter } from '../../hooks/useNotificationCenter';
import { clsx } from 'clsx';
import {
  Bars3Icon,
  XMarkIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
// Solid/filled icons for sidebar - these have the icon shape filled with color
import {
  HomeIcon,
  UsersIcon,
  CurrencyDollarIcon,
  AcademicCapIcon,
  BookOpenIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  DocumentDuplicateIcon,
  ClipboardDocumentListIcon,
  UserPlusIcon,
  FunnelIcon,
  ChatBubbleLeftRightIcon,
  ArchiveBoxIcon,
  BeakerIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/solid';
import { Sidebar, CommandPalette, Breadcrumbs, Avatar, TourGuide, PlayfulToastSettings, NotificationBell, type NavGroup } from '../ui';
import salesToastLogo from '../../assets/images/sales-toast-logo.png';

const DashboardLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [playfulToastSettingsOpen, setPlayfulToastSettingsOpen] = useState(false);
  const { user, logout } = useAuth();
  const notificationCenter = useNotificationCenter();
  const navigate = useNavigate();

  // Navigation configuration with groups
  const navigation: NavGroup[] = [
    {
      name: 'Main',
      items: [
        { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, iconColor: 'text-indigo-500' },
        { name: 'Training', href: '/training', icon: AcademicCapIcon, iconColor: 'text-pink-500' },
        {
          name: 'Chat',
          href: '/chat',
          icon: ChatBubbleLeftRightIcon,
          iconColor: 'text-cyan-500',
          badge: notificationCenter.chatUnreadCount > 0 ? notificationCenter.chatUnreadCount : undefined,
        },
      ],
    },
    {
      name: 'CRM',
      items: [
        {
          name: 'Clients',
          href: '/clients',
          icon: UsersIcon,
          iconColor: 'text-blue-500',
          children: [
            { name: 'All Clients', href: '/clients', icon: UsersIcon },
            { name: 'Segments', href: '/segments', icon: FunnelIcon },
            { name: 'Email Templates', href: '/clients/templates', icon: DocumentTextIcon },
            { name: 'Email History', href: '/clients/emails', icon: EnvelopeIcon },
          ],
        },
        { name: 'Deals', href: '/deals', icon: CurrencyDollarIcon, iconColor: 'text-emerald-500' },
        { name: 'Samples', href: '/samples', icon: BeakerIcon, iconColor: 'text-violet-500' },
        { name: 'Blacklist', href: '/blacklist', icon: ShieldExclamationIcon, iconColor: 'text-rose-500' },
        { name: 'Quotes', href: '/quotes', icon: DocumentDuplicateIcon, iconColor: 'text-cyan-500' },
        { name: 'Tasks', href: '/tasks', icon: ClipboardDocumentListIcon, iconColor: 'text-purple-500' },
      ],
    },
    {
      name: 'Resources',
      items: [
        {
          name: 'Catalogue',
          href: '/catalogue',
          icon: BookOpenIcon,
          iconColor: 'text-orange-500',
        },
        {
          name: 'Inventory',
          href: '/inventory',
          icon: ArchiveBoxIcon,
          iconColor: 'text-emerald-500',
        },
        {
          name: 'Invoicing',
          href: '/invoicing',
          icon: DocumentTextIcon,
          iconColor: 'text-indigo-500',
        },
      ],
    },
  ];

  // Quick actions for the sidebar
  const quickActions = [
    {
      name: 'New Client',
      icon: UserPlusIcon,
      onClick: () => navigate('/clients/new'),
    },
    {
      name: 'New Deal',
      icon: CurrencyDollarIcon,
      onClick: () => navigate('/deals/new'),
    },
    {
      name: 'New Task',
      icon: ClipboardDocumentListIcon,
      onClick: () => navigate('/tasks'),
    },
  ];

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const openCommandPalette = useCallback(() => {
    setCommandPaletteOpen(true);
  }, []);

  const openPlayfulToastSettings = useCallback(() => {
    setPlayfulToastSettingsOpen(true);
  }, []);

  const logoIcon = (
    <img src={salesToastLogo} alt="Sales Toast" className="w-8 h-8 object-contain" />
  );

  return (
    <div className="h-screen flex overflow-hidden bg-gradient-to-br from-neutral-50 to-neutral-100">
      {/* Mobile sidebar overlay */}
      <div
        className={clsx(
          'fixed inset-0 z-40 md:hidden transition-opacity duration-300',
          sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
      >
        <div
          className="fixed inset-0 bg-neutral-900/60 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
        <div
          className={clsx(
            'fixed inset-y-0 left-0 flex flex-col w-[280px] bg-white shadow-2xl transition-transform duration-300',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          <div className="absolute top-4 right-4">
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-lg hover:bg-neutral-100 transition-colors"
            >
              <XMarkIcon className="h-5 w-5 text-neutral-500" />
            </button>
          </div>
          <Sidebar
            navigation={navigation}
            user={user}
            logo={{ icon: logoIcon, title: 'Sales Toast' }}
            onLogout={handleLogout}
            onOpenCommandPalette={openCommandPalette}
            onOpenSettings={openPlayfulToastSettings}
            quickActions={quickActions}
            storageKey="dashboardSidebarCollapsed"
          />
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:block flex-shrink-0">
        <Sidebar
          navigation={navigation}
          user={user}
          logo={{ icon: logoIcon, title: 'Sales Toast' }}
          onLogout={handleLogout}
          onOpenCommandPalette={openCommandPalette}
          onOpenSettings={openPlayfulToastSettings}
          quickActions={quickActions}
          storageKey="dashboardSidebarCollapsed"
        />
      </div>

      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Desktop header */}
        <header className="hidden md:flex items-center justify-between h-16 px-6 bg-white border-b border-neutral-200 shadow-sm flex-shrink-0">
          <Breadcrumbs showHome={true} homeHref="/dashboard" />

          <div className="flex items-center gap-3">
            {/* Search button that opens command palette */}
            <button
              onClick={openCommandPalette}
              className={clsx(
                'flex items-center gap-2 px-3 py-2 rounded-lg',
                'bg-neutral-100 hover:bg-neutral-200',
                'text-neutral-500 text-sm',
                'transition-all duration-200',
                'border border-neutral-200 hover:border-neutral-300',
                'focus:outline-none focus:ring-2 focus:ring-primary-500'
              )}
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Search...</span>
              <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-neutral-400 bg-neutral-200 rounded">
                <span className="text-xs">⌘</span>K
              </kbd>
            </button>

            {/* Playful Toast Settings */}
            <button
              onClick={openPlayfulToastSettings}
              className="relative p-2 rounded-lg text-neutral-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
              title="Playful Toast Settings"
            >
              <SparklesIcon className="h-5 w-5" />
            </button>

            <NotificationBell
              chatHref="/chat"
              chatUnreadCount={notificationCenter.chatUnreadCount}
              systemUnreadCount={notificationCenter.systemUnreadCount}
              totalUnreadCount={notificationCenter.totalUnreadCount}
              notifications={notificationCenter.systemNotifications}
              loading={notificationCenter.loading}
              onRefresh={notificationCenter.refresh}
              onMarkRead={notificationCenter.markNotificationRead}
              onMarkAllRead={notificationCenter.markAllSystemRead}
            />

            {/* User quick profile */}
            <div className="flex items-center gap-2 pl-3 border-l border-neutral-200">
              <Avatar name={`${user?.firstName || ''} ${user?.lastName || ''}`} size="sm" />
              <div className="hidden lg:block">
                <p className="text-sm font-medium text-neutral-900">
                  {user?.firstName} {user?.lastName}
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Mobile header */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 bg-white shadow-sm flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="h-9 w-9 bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/25">
            {logoIcon}
          </div>

          <NotificationBell
            chatHref="/chat"
            chatUnreadCount={notificationCenter.chatUnreadCount}
            systemUnreadCount={notificationCenter.systemUnreadCount}
            totalUnreadCount={notificationCenter.totalUnreadCount}
            notifications={notificationCenter.systemNotifications}
            loading={notificationCenter.loading}
            size="lg"
            onRefresh={notificationCenter.refresh}
            onMarkRead={notificationCenter.markNotificationRead}
            onMarkAllRead={notificationCenter.markAllSystemRead}
          />
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-neutral-50">
          <Outlet />
        </main>
      </div>

      {/* Command Palette - Single instance, controlled */}
      <CommandPalette
        isAdmin={false}
        isOpen={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        showTrigger={false}
      />

      {/* Tour Guide - Help button with feature tour */}
      <TourGuide isAdmin={false} />

      {/* Playful Toast Settings Modal */}
      <PlayfulToastSettings
        isOpen={playfulToastSettingsOpen}
        onClose={() => setPlayfulToastSettingsOpen(false)}
      />
    </div>
  );
};

export default DashboardLayout;
